import { DownOutlined } from '@ant-design/icons';
import { Popover, Tree } from 'antd';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { AppState } from '../../app-state';
import { PrimaryButton } from '../../common/components/primary-button.component';
import {
  AnnotationCategory,
  AnnotationSchema,
} from '../../annotations/models/annotation-schema.model';
import { selectActiveAnnotationSchema } from '../../annotations/selectors/annotation-schema.selectors';

const TreeNode = Tree.TreeNode;
const CATEGORY_PREFIX = 'category-';

const PopoverContent = styled.div`
  width: 300px;
  max-height: 400px;
  overflow-y: auto;
`;

const ToggleLink = styled.a`
  font-size: 12px;
  margin-bottom: 4px;
  display: inline-block;
`;

type AnnotationTypeSelectorProps = {
  schema: AnnotationSchema | null;
  selectedTypes: string[];
  onSelectionChange: (types: string[]) => void;
  disabled?: boolean;
};

const AnnotationTypeSelectorUnconnected = (
  props: AnnotationTypeSelectorProps,
) => {
  const [visible, setVisible] = useState(false);
  const { schema, selectedTypes, onSelectionChange, disabled } = props;

  if (!schema) return null;

  const onCheck = (
    checkedKeys:
      | (string | number)[]
      | { checked: (string | number)[]; halfChecked: (string | number)[] },
  ) => {
    const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    const typeKeys = keys
      .map(k => String(k))
      .filter(k => !k.startsWith(CATEGORY_PREFIX));
    onSelectionChange(typeKeys);
  };

  const allTypeKeys = schema.annotationTypes.map(t => t.key);
  const allSelected = allTypeKeys.every(k => selectedTypes.indexOf(k) >= 0);

  const content = (
    <PopoverContent>
      <ToggleLink
        onClick={() => onSelectionChange(allSelected ? [] : allTypeKeys)}
      >
        {allSelected ? 'Deselect all' : 'Select all'}
      </ToggleLink>
      <Tree checkable checkedKeys={selectedTypes} onCheck={onCheck}>
        {schema.categories.map((cat: AnnotationCategory) => (
          <TreeNode
            title={cat.title}
            key={`${CATEGORY_PREFIX}${cat.annotationCategoryId}`}
          >
            {cat.annotationTypes.map(t => (
              <TreeNode title={t.title} key={t.key} />
            ))}
          </TreeNode>
        ))}
      </Tree>
    </PopoverContent>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      visible={visible}
      onVisibleChange={setVisible}
    >
      <PrimaryButton onClick={() => setVisible(!visible)} disabled={disabled}>
        <DownOutlined />
      </PrimaryButton>
    </Popover>
  );
};

export const AnnotationTypeSelector = connect((state: AppState) => ({
  schema: selectActiveAnnotationSchema(state),
}))(AnnotationTypeSelectorUnconnected);
